import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Top-level error boundary.  When the SPA crashes during render we
 * surface the error in the page itself instead of leaving the user
 * with a blank screen.  In dev this also makes typos / missing imports
 * trivial to debug without DevTools.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged so the message also shows up in the DevTools console.
    // eslint-disable-next-line no-console
    console.error("[ANC ErrorBoundary]", error, info);
    this.setState({ error, info });
  }

  override render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }
    return (
      <div className="auth-shell">
        <div
          className="auth-card"
          style={{ textAlign: "left", maxWidth: 720 }}
        >
          <div className="auth-card__brand" style={{ margin: "0 0 16px" }}>
            ⚠️
          </div>
          <h1>La página no pudo iniciar</h1>
          <p className="text-muted">
            React lanzó una excepción al renderizar la SPA. El detalle está
            abajo y también en la consola del navegador.
          </p>
          <pre
            style={{
              background: "#0e2230",
              color: "#cde9f3",
              padding: 16,
              borderRadius: 8,
              fontSize: 12,
              overflowX: "auto",
            }}
          >
            {this.state.error.name}: {this.state.error.message}
            {"\n\n"}
            {this.state.info?.componentStack ?? this.state.error.stack}
          </pre>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => window.location.reload()}
          >
            Recargar
          </button>
        </div>
      </div>
    );
  }
}
